
import { useEffect, useRef, useState } from "react";
import { Candidate, CandidateStatus, RoleStatus } from "../../../Models";
import { checkAvailableSlots, GetAvailableAssessmentSlots, GetAvailableInterviewSlots, AvailableAssessmentSlot } from "../../../api/roles.api";
import { CandidateAction } from "./RoleCandidates";
import {
  ArrowLongRightIcon,
  CheckCircleIcon,
} from "@heroicons/react/20/solid";
import {
  CalendarDaysIcon,
  UserGroupIcon,
  UsersIcon,
  InboxIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { useParams } from "react-router-dom";
import useApiToken from "../../../hooks/useApiToken";
type Props = {
  candidatesWithAction: CandidateAction[];
  handleCandidateWithAction: (candidate: Candidate[]) => void;
  roleStatuses: RoleStatus[];
  setSelectedCandidate: React.Dispatch<React.SetStateAction<CandidateAction[]>>;
  bulkActionIsDisqualify: boolean;
  handleBulkConfirmButtonDisabled: (query: boolean) => void;
  onPanelsSelected: (panels: string[]) => void;
};
export type AvailableSlot = {
  roleStatusId: string;
  roleStatusName: string;
  adjustedCount: number;
  selectedCount: number | null;
};

type AssignMode = "candidate" | "recruiter";

const formatSlotRange = (startIso: string, endIso: string) => {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const dateOpts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const timeOpts: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };
  return {
    date: start.toLocaleDateString(undefined, dateOpts),
    time: `${start.toLocaleTimeString(undefined, timeOpts)} - ${end.toLocaleTimeString(undefined, timeOpts)}`
  };
};

function ToggleSwitch({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <label className={`relative inline-flex items-center ${disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}>
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <div className="w-10 h-6 rounded-full bg-gray-300 dark:bg-gray-600 peer-checked:bg-entntblue transition-all duration-200 peer-focus:ring-4 peer-focus:ring-entntblue/20" />
      <div className="absolute left-[2px] top-[2px] w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200 peer-checked:translate-x-4" />
    </label>
  );
}

export default function BulkActionModalDescription({
  candidatesWithAction,
  handleCandidateWithAction,
  roleStatuses,
  bulkActionIsDisqualify,
  handleBulkConfirmButtonDisabled,
  onPanelsSelected,
}: Props) {
  const actionIndex = bulkActionIsDisqualify ? 1 : 0;
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
  const [panelGroups, setPanelGroups] = useState<{ panel: string; count: number }[]>([]);
  const [selectedPanels, setSelectedPanels] = useState<Set<string>>(new Set());
  const isInitialLoad = useRef(true);
  const { roleId } = useParams();
  const token = useApiToken();

  const [assignMode, setAssignMode] = useState<AssignMode>("candidate");
  const [detailedAssessmentSlots, setDetailedAssessmentSlots] = useState<AvailableAssessmentSlot[]>([]);
  const [detailedInterviewSlots, setDetailedInterviewSlots] = useState<any[]>([]);
  const [selectedAssessmentSlotId, setSelectedAssessmentSlotId] = useState<string>("");
  const [interviewAssignments, setInterviewAssignments] = useState<Record<string, string>>({});
  const [forceExpandAssessment, setForceExpandAssessment] = useState(false);
  const [interviewAutoAssignAttempted, setInterviewAutoAssignAttempted] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<StageBucket, boolean>>({
    assessment: false,
    interview: false,
    other: false,
  });
  const [revealedExceptions, setRevealedExceptions] = useState<Record<StageBucket, boolean>>({
    assessment: false,
    interview: false,
    other: false,
  });

  useEffect(() => {
    if (!token) return;
    const fetchAvailableSlots = async () => {
      if (!roleId) return;
      try {
        const slots = await checkAvailableSlots(roleId, token);
        setAvailableSlots(slots);
      } catch (error) {
        console.error("Error fetching available slots");
        handleBulkConfirmButtonDisabled(true);
      }
    };

    fetchAvailableSlots();
  }, [roleId, token]);

  useEffect(() => {
    if (!token || !roleId) return;
    GetAvailableAssessmentSlots(roleId, token)
      .then((slots) => setDetailedAssessmentSlots(slots || []))
      .catch(() => setDetailedAssessmentSlots([]));
    GetAvailableInterviewSlots(token, roleId)
      .then((slots) => setDetailedInterviewSlots(slots || []))
      .catch(() => setDetailedInterviewSlots([]));
  }, [roleId, token]);

  useEffect(() => {
    // Panels are derived from the truly available (unbooked, future) interview slots,
    // so the panel selector only appears when there's actually something to book.
    const panelMap = new Map<string, number>();
    detailedInterviewSlots.forEach((s: any) => {
      const panel = s.interviewerEmail || "Unassigned";
      panelMap.set(panel, (panelMap.get(panel) || 0) + 1);
    });
    const groups = Array.from(panelMap.entries())
      .map(([panel, count]) => ({ panel, count }))
      .sort((a, b) => a.panel.localeCompare(b.panel));
    setPanelGroups(groups);

    if (groups.length > 0) {
      const allPanels = new Set(groups.map((g) => g.panel));
      setSelectedPanels(allPanels);
      onPanelsSelected(Array.from(allPanels));
    } else {
      setSelectedPanels(new Set());
      onPanelsSelected([]);
    }
  }, [detailedInterviewSlots]);

  useEffect(() => {
    if (availableSlots.length > 0) {
      if (isInitialLoad.current) {
        const updatedCandidates = candidatesWithAction.map((sc) => {
          const actionIndex = bulkActionIsDisqualify ? 1 : 0;
          if (
            availableSlots.some(
              (slot) =>
                slot.roleStatusId === sc.actions?.[actionIndex]?.roleStatusId
            ) &&
            sc.actions![actionIndex].email === true
          ) {
            sc.actions![actionIndex].slots = true;
            // set panel if a specific panel is selected
              if (selectedPanels && selectedPanels.size === 1) {
                const sp = Array.from(selectedPanels)[0];
                if (sp && sp !== "All") {
                  sc.actions![actionIndex].panel = sp;
                }
              }
          }
          return sc;
        });

        handleCandidateWithAction(updatedCandidates);
        updateSelectedSlotsCount(availableSlots, updatedCandidates);
        isInitialLoad.current = false;
      }
    } else {
      handleBulkConfirmButtonDisabled(false);
    }
  }, [availableSlots, selectedPanels]);

  const updateSelectedSlotsCount = (
    slots: AvailableSlot[],
    updatedCandidates: CandidateAction[] = candidatesWithAction
  ) => {
    let invalidSelections = false;
    const updatedSlots = slots.map((slot) => {
      const count = updatedCandidates.filter((candidate) => {
        const nextStatus = candidate.actions?.[bulkActionIsDisqualify ? 1 : 0];
        return (
          nextStatus &&
          nextStatus.slots &&
          nextStatus.roleStatusId === slot.roleStatusId
        );
      }).length;

      if (count > slot.adjustedCount) {
        invalidSelections = true;
      }

      return {
        ...slot,
        selectedCount: count,
      };
    });

    handleBulkConfirmButtonDisabled(invalidSelections);
    setAvailableSlots(updatedSlots);
  };

  const assessmentRoleStatusIds = new Set(detailedAssessmentSlots.map((s) => s.roleStatusId));
  const interviewSlotsForStatus = (roleStatusId: string | undefined) =>
    detailedInterviewSlots.filter((s: any) => s.roleStatusId === roleStatusId);
  const hasAnyDetailedSlots = detailedAssessmentSlots.length > 0 || detailedInterviewSlots.length > 0;
  const selectedAssessmentSlot = detailedAssessmentSlots.find((s) => s.id === selectedAssessmentSlotId);
  const assessmentPickerCollapsed = Boolean(selectedAssessmentSlot) && !forceExpandAssessment;

  const applyRecruiterAssignments = (
    candidates: CandidateAction[],
    assessmentSlotId: string,
    interviewMap: Record<string, string>
  ) => {
    let missingInterviewSlot = false;
    let missingAssessmentSlot = false;
    const updated = candidates.map((sc) => {
      if (!sc.actions) return sc;
      const action = sc.actions[actionIndex];
      if (!action) return sc;
      if (!action.email) {
        sc.actions[actionIndex] = {
          ...action,
          slots: false,
          recruiterAssignedSlotId: undefined,
          recruiterAssignedSlotType: undefined,
        };
        return sc;
      }

      const needsAssessment = assessmentRoleStatusIds.has(action.roleStatusId);
      const matchingInterviewSlots = interviewSlotsForStatus(action.roleStatusId);
      const needsInterview = matchingInterviewSlots.length > 0;

      let recruiterAssignedSlotId: string | undefined;
      let recruiterAssignedSlotType: "interview" | "assessment" | undefined;

      if (needsAssessment) {
        if (assessmentSlotId) {
          recruiterAssignedSlotId = assessmentSlotId;
          recruiterAssignedSlotType = "assessment";
        } else {
          missingAssessmentSlot = true;
        }
      } else if (needsInterview) {
        const chosen = interviewMap[sc.id];
        if (chosen) {
          recruiterAssignedSlotId = chosen;
          recruiterAssignedSlotType = "interview";
        } else {
          missingInterviewSlot = true;
        }
      }

      sc.actions[actionIndex] = {
        ...action,
        slots: false,
        recruiterAssignedSlotId,
        recruiterAssignedSlotType,
      };
      return sc;
    });

    handleCandidateWithAction(updated);
    handleBulkConfirmButtonDisabled(missingInterviewSlot || missingAssessmentSlot);
  };

  const handleModeChange = (mode: AssignMode) => {
    setAssignMode(mode);
    if (mode === "candidate") {
      const updated = candidatesWithAction.map((sc) => {
        if (!sc.actions) return sc;
        const action = sc.actions[actionIndex];
        if (!action) return sc;
        sc.actions[actionIndex] = {
          ...action,
          recruiterAssignedSlotId: undefined,
          recruiterAssignedSlotType: undefined,
        };
        return sc;
      });
      handleCandidateWithAction(updated);
      updateSelectedSlotsCount(availableSlots, updated);
    } else {
      setSelectedAssessmentSlotId("");
      setInterviewAssignments({});
      setInterviewAutoAssignAttempted(false);
      applyRecruiterAssignments(candidatesWithAction, "", {});
    }
  };

  const handleAssessmentSlotChange = (slotId: string) => {
    setSelectedAssessmentSlotId(slotId);
    setForceExpandAssessment(false);
    applyRecruiterAssignments(candidatesWithAction, slotId, interviewAssignments);
  };

  const handleInterviewSlotChange = (candidateId: string, slotId: string) => {
    const next = { ...interviewAssignments };
    if (slotId) {
      next[candidateId] = slotId;
    } else {
      delete next[candidateId];
    }
    setInterviewAssignments(next);
    applyRecruiterAssignments(candidatesWithAction, selectedAssessmentSlotId, next);
  };

  const handleAutoAssignInterviewSlots = () => {
    const next = { ...interviewAssignments };
    const usedSlotIds = new Set(Object.values(next));

    const groups = new Map<string, CandidateAction[]>();
    bucketedCandidates.interview.forEach((sc) => {
      const action = sc.actions?.[actionIndex];
      if (!action?.email) return;
      if (interviewSlotsForStatus(action.roleStatusId).length === 0) return;
      const group = groups.get(action.roleStatusId) ?? [];
      group.push(sc);
      groups.set(action.roleStatusId, group);
    });

    groups.forEach((candidatesInGroup, roleStatusId) => {
      const sortedSlots = interviewSlotsForStatus(roleStatusId)
        .filter((slot: any) => !usedSlotIds.has(slot.id))
        .sort(
          (a: any, b: any) =>
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
        );

      let slotIdx = 0;
      candidatesInGroup.forEach((sc) => {
        if (next[sc.id]) return;
        const slot = sortedSlots[slotIdx];
        if (!slot) return;
        next[sc.id] = slot.id;
        usedSlotIds.add(slot.id);
        slotIdx++;
      });
    });

    setInterviewAssignments(next);
    setInterviewAutoAssignAttempted(true);
    applyRecruiterAssignments(candidatesWithAction, selectedAssessmentSlotId, next);
  };

  const handleToggleEmail = (id: string) => {
    const updatedCandidates = candidatesWithAction.map((sc) => {
      if (id === sc.id && sc.actions) {
        const newEmailState = !sc.actions[actionIndex].email;
        let newSlotsState = sc.actions[actionIndex].slots;

        if (assignMode === "candidate") {
          // If enabling email, check if we should also enable slots
          if (newEmailState) {
            const hasAvailableSlots = availableSlots.some(
              (slot) =>
                slot.roleStatusId === sc.actions?.[actionIndex]?.roleStatusId
            );
            if (hasAvailableSlots) {
              newSlotsState = true;
            }
          } else {
            // If disabling email, disable slots
            newSlotsState = false;
          }
        }

        sc.actions[actionIndex] = {
          ...sc.actions[actionIndex],
          email: newEmailState,
          slots: newSlotsState
        };
      }
      return sc;
    });

    if (assignMode === "recruiter") {
      applyRecruiterAssignments(updatedCandidates, selectedAssessmentSlotId, interviewAssignments);
    } else {
      updateSelectedSlotsCount(availableSlots, updatedCandidates);
      handleCandidateWithAction(updatedCandidates);
    }
  };
  const handleToggleSlots = (id: string) => {
    const updatedCandidates = candidatesWithAction.map((sc) => {
      if (id === sc.id && sc.actions) {
        sc.actions[actionIndex] = {
          ...sc.actions[actionIndex],
          slots:
            sc.actions[actionIndex].email === true
              ? !sc.actions[actionIndex].slots
              : false,
        };
      }
      return sc;
    });
    handleCandidateWithAction(updatedCandidates);
    updateSelectedSlotsCount(availableSlots, updatedCandidates);
  };

  const handlePanelToggle = (panel: string) => {
    const newSelected = new Set(selectedPanels);
    if (newSelected.has(panel)) {
      newSelected.delete(panel);
    } else {
      newSelected.add(panel);
    }
    setSelectedPanels(newSelected);
    // Explicitly pass the array from the new local Set
    onPanelsSelected(Array.from(newSelected));
  };

  const handleSelectAll = () => {
    if (selectedPanels.size === panelGroups.length) {
      setSelectedPanels(new Set());
      onPanelsSelected([]);
    } else {
      const allPanels = new Set(panelGroups.map(g => g.panel));
      setSelectedPanels(allPanels);
      onPanelsSelected(Array.from(allPanels));
    }
  };

  const getStatusName = (candidate: CandidateAction) => {
    if (!candidate.actions) return "";
    const action = candidate.actions[actionIndex];
    const roleStatus = roleStatuses.find((rs) => rs.id === action.roleStatusId);
    return roleStatus ? roleStatus.name : "";
  };

  type StageBucket = "assessment" | "interview" | "other";

  const classifyBucket = (action?: CandidateStatus): StageBucket => {
    if (!action) return "other";
    const rs = roleStatuses.find((r) => r.id === action.roleStatusId);

    // Authoritative: the stage's own configured type, when set.
    const typeName = rs?.roleStatusType?.name;
    if (typeName === "Assessment") return "assessment";
    if (typeName === "Interview") return "interview";
    if (typeName) return "other"; // e.g. "Assignment" — explicitly not slot-based

    // RoleStatusTypeId isn't set on this stage (older role) — fall back to name, then slot presence.
    const name = rs?.name?.toLowerCase() ?? "";
    if (name.includes("assessment")) return "assessment";
    if (name.includes("interview")) return "interview";

    if (assessmentRoleStatusIds.has(action.roleStatusId)) return "assessment";
    if (interviewSlotsForStatus(action.roleStatusId).length > 0) return "interview";
    return "other";
  };

  const getActionOrder = (action?: CandidateStatus) => {
    const rs = action ? roleStatuses.find((r) => r.id === action.roleStatusId) : undefined;
    return rs?.order ?? Number.MAX_SAFE_INTEGER;
  };

  const bucketedCandidates: Record<StageBucket, CandidateAction[]> = {
    assessment: [],
    interview: [],
    other: [],
  };
  candidatesWithAction.forEach((sc) => {
    bucketedCandidates[classifyBucket(sc.actions?.[actionIndex])].push(sc);
  });

  const bucketMinOrder = (bucket: StageBucket) => {
    const members = bucketedCandidates[bucket];
    if (members.length === 0) return Number.MAX_SAFE_INTEGER;
    return Math.min(...members.map((sc) => getActionOrder(sc.actions?.[actionIndex])));
  };

  const interviewEligibleCandidates = bucketedCandidates.interview.filter((sc) => {
    const action = sc.actions?.[actionIndex];
    return !!action?.email && interviewSlotsForStatus(action.roleStatusId).length > 0;
  });
  const interviewTotalCount = interviewEligibleCandidates.length;
  const interviewAssignedCount = interviewEligibleCandidates.filter((sc) => interviewAssignments[sc.id]).length;
  const interviewUnassignedCount = interviewTotalCount - interviewAssignedCount;

  const sectionsToRender = (["assessment", "interview", "other"] as StageBucket[])
    .filter((bucket) => bucketedCandidates[bucket].length > 0)
    .sort((a, b) => bucketMinOrder(a) - bucketMinOrder(b));

  const bucketLabel: Record<StageBucket, string> = {
    assessment: "Advancing to Assessment",
    interview: "Advancing to Interview",
    other: "Advancing to Other Stages",
  };

  const toggleSectionExpanded = (bucket: StageBucket) => {
    setExpandedSections((prev) => ({ ...prev, [bucket]: !prev[bucket] }));
    // collapsing a section hides its exceptions too, so it re-collapses cleanly next time it's expanded
    setRevealedExceptions((prev) => ({ ...prev, [bucket]: false }));
  };

  const toggleExceptionsRevealed = (bucket: StageBucket) => {
    setRevealedExceptions((prev) => ({ ...prev, [bucket]: !prev[bucket] }));
  };

  // Splits members into a majority/minority by `isOn`, treating a side as a callable-out
  // "exception" only when it's a clear minority (<=25%) — an even split isn't an exception,
  // it's just two groups, so callers fall back to plain counts in that case.
  const splitByMinority = (members: CandidateAction[], isOn: (sc: CandidateAction) => boolean) => {
    const onMembers = members.filter(isOn);
    const offMembers = members.filter((sc) => !isOn(sc));
    const total = members.length;
    let minority: CandidateAction[] = [];
    if (total > 0 && offMembers.length > 0 && offMembers.length / total <= 0.25) {
      minority = offMembers;
    } else if (total > 0 && onMembers.length > 0 && onMembers.length / total <= 0.25) {
      minority = onMembers;
    }
    return { onCount: onMembers.length, offCount: offMembers.length, minority };
  };

  const emailIsOn = (sc: CandidateAction) => !!sc.actions?.[actionIndex]?.email;
  const slotsIsOn = (sc: CandidateAction) => !!sc.actions?.[actionIndex]?.slots;

  const dedupeMembers = (members: CandidateAction[], groups: CandidateAction[][]) => {
    const ids = new Set(groups.flat().map((sc) => sc.id));
    return members.filter((sc) => ids.has(sc.id));
  };

  const renderRecruiterRow = (sc: CandidateAction, bucket: StageBucket) => {
    const action = sc.actions?.[actionIndex];
    const stageName = getStatusName(sc);
    const hasAssessmentSlot = action ? assessmentRoleStatusIds.has(action.roleStatusId) : false;
    const matchingInterviewSlots = action ? interviewSlotsForStatus(action.roleStatusId) : [];
    const hasInterviewSlot = matchingInterviewSlots.length > 0;
    const needsSelection = !!action?.email && bucket === "interview" && hasInterviewSlot && !interviewAssignments[sc.id];
    const usedByOthers = new Set(
      Object.entries(interviewAssignments)
        .filter(([candidateId]) => candidateId !== sc.id)
        .map(([, slotId]) => slotId)
    );

    return (
      <li
        key={sc.id}
        className="flex justify-between items-center gap-2 px-3 py-2.5 bg-white rounded-2xl shadow-[0_4px_15px_rgb(0,0,0,0.04)]"
      >
        {sc.actions && (
          <>
            <span className="flex items-center gap-2 text-sm text-gray-900 min-w-0 flex-1">
              <span className="flex-shrink-0 h-8 w-8 rounded-full bg-slate-900 text-white text-xs font-semibold shadow-[0_4px_10px_rgb(0,0,0,0.2)] flex items-center justify-center">
                {sc.firstName?.[0]?.toUpperCase()}
                {sc.lastName?.[0]?.toUpperCase()}
              </span>
              <span className="truncate font-semibold text-xs min-w-0">{`${sc.firstName} ${sc.lastName}`}</span>
              <ArrowLongRightIcon className="h-3 w-3 text-blue-600 flex-shrink-0 mx-0.5" />
              <span className="inline-flex flex-shrink-0 items-center rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                {getStatusName(sc)}
              </span>
            </span>
            <div className="flex items-center justify-end gap-3 flex-shrink-0">
              {action?.email && (
                <>
                  {bucket === "assessment" ? (
                    hasAssessmentSlot ? (
                      <span className="text-xs text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">uses assessment slot</span>
                    ) : (
                      <span
                        className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded italic"
                        title={`No assessment slots have been created for "${stageName}" yet. Create slots first, or use "Let candidates choose" instead.`}
                      >
                        no slots yet
                      </span>
                    )
                  ) : bucket === "interview" ? (
                    hasInterviewSlot ? (
                      <div className="relative inline-block">
                        <select
                          className={`appearance-none text-xs font-medium rounded-lg py-1 pl-2 pr-6 max-w-[11rem] focus:outline-none focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 shadow-sm border-2 ${
                            needsSelection
                              ? "border-amber-400 bg-amber-50 text-amber-900"
                              : "border-green-300 bg-green-50 text-green-900"
                          }`}
                          value={interviewAssignments[sc.id] || ""}
                          onChange={(e) => handleInterviewSlotChange(sc.id, e.target.value)}
                        >
                          <option value="">Select a slot</option>
                          {matchingInterviewSlots
                            .filter((slot: any) => !usedByOthers.has(slot.id))
                            .map((slot: any) => (
                              <option key={slot.id} value={slot.id}>
                                {formatSlotRange(slot.startTime, slot.endTime).date}, {formatSlotRange(slot.startTime, slot.endTime).time}
                                {slot.interviewerEmail ? ` (${slot.interviewerEmail})` : ""}
                              </option>
                            ))}
                        </select>
                        <ChevronDownIcon
                          className={`pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${
                            needsSelection ? "text-amber-600" : "text-green-600"
                          }`}
                        />
                      </div>
                    ) : (
                      <span
                        className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded italic"
                        title={`No interview slots have been created for "${stageName}" yet. Create slots first, or use "Let candidates choose" instead.`}
                      >
                        no slots yet
                      </span>
                    )
                  ) : (
                    <span className="text-xs text-gray-400 italic">no slot required</span>
                  )}
                </>
              )}
              {!action?.email && (
                <span className="text-sm text-gray-400">—</span>
              )}
              <ToggleSwitch
                checked={!!sc.actions[actionIndex].email}
                onChange={() => handleToggleEmail(sc.id)}
              />
            </div>
          </>
        )}
      </li>
    );
  };

  const renderCandidateModeRow = (sc: CandidateAction) => (
    <li
      key={sc.id}
      className="flex justify-between items-center gap-2 px-3 py-2.5 bg-white rounded-2xl shadow-[0_4px_15px_rgb(0,0,0,0.04)]"
    >
      {sc.actions && (
        <>
          <span className="flex items-center gap-2 text-sm text-gray-900 min-w-0 flex-1">
            <span className="flex-shrink-0 h-8 w-8 rounded-full bg-slate-900 text-white text-xs font-semibold shadow-[0_4px_10px_rgb(0,0,0,0.2)] flex items-center justify-center">
              {sc.firstName?.[0]?.toUpperCase()}
              {sc.lastName?.[0]?.toUpperCase()}
            </span>
            <span className="truncate font-semibold text-xs min-w-0">{`${sc.firstName} ${sc.lastName}`}</span>
            <ArrowLongRightIcon className="h-3 w-3 text-blue-600 flex-shrink-0 mx-0.5" />
            <span className="inline-flex flex-shrink-0 items-center rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
              {getStatusName(sc)}
            </span>
          </span>
          <div className="flex items-center justify-end gap-3 flex-shrink-0">
            <ToggleSwitch
              checked={!!sc.actions[actionIndex].slots}
              onChange={() => handleToggleSlots(sc.id)}
              disabled={availableSlots.length === 0}
            />
            <ToggleSwitch
              checked={!!sc.actions[actionIndex].email}
              onChange={() => handleToggleEmail(sc.id)}
            />
          </div>
        </>
      )}
    </li>
  );

  const renderSectionHeader = (bucket: StageBucket, count: number) => (
    <button
      type="button"
      onClick={() => toggleSectionExpanded(bucket)}
      className="flex w-full items-center justify-between gap-2 rounded-full bg-white px-3 py-2 shadow-sm mb-2"
    >
      <span className="flex items-center gap-2 font-bold text-base text-gray-900 dark:text-white">
        {expandedSections[bucket] ? (
          <ChevronDownIcon className="h-4 w-4 text-gray-600 flex-shrink-0" />
        ) : (
          <ChevronRightIcon className="h-4 w-4 text-gray-600 flex-shrink-0" />
        )}
        {bucket === "interview" ? (
          <UserGroupIcon className="h-5 w-5 text-entntblue flex-shrink-0" />
        ) : (
          <CalendarDaysIcon className="h-5 w-5 text-entntblue flex-shrink-0" />
        )}
        {bucketLabel[bucket]}
      </span>
      <span className="text-[10px] font-bold bg-gray-300 text-gray-900 px-2 py-0.5 rounded-full flex-shrink-0">
        {count} candidate{count === 1 ? "" : "s"}
      </span>
    </button>
  );

  const renderColumnHeader = (slotColumnLabel: string) => (
    <div className="flex items-center justify-between mt-2 mb-1.5 px-3 bg-white py-2 rounded-full shadow-sm mx-1">
      <span className="font-bold text-[10px] uppercase tracking-wider text-gray-500 flex-shrink-0">Candidate</span>
      <div className="flex items-center gap-4 pr-1 flex-shrink-0">
        <span className="font-bold text-[10px] uppercase tracking-wider text-gray-500">{slotColumnLabel}</span>
        <span className="font-bold text-[10px] uppercase tracking-wider text-gray-500">Email</span>
      </div>
    </div>
  );

  const describeSplit = (label: string, split: { onCount: number; offCount: number }, total: number) => {
    if (split.offCount === 0) return `${label} on for all ${total}`;
    if (split.onCount === 0) return `${label} off for all ${total}`;
    return `${label} on for ${split.onCount}, off for ${split.offCount}`;
  };

  const renderExceptionsLink = (bucket: StageBucket, label: string) => (
    <button
      type="button"
      onClick={() => toggleExceptionsRevealed(bucket)}
      className="inline-flex items-center rounded-full border border-entntblue px-2 py-0.5 text-[11px] font-semibold text-entntblue hover:bg-entntblue hover:text-white transition-colors"
    >
      {revealedExceptions[bucket] ? "hide" : label}
    </button>
  );

  return (
    <div className="text-left">
      {hasAnyDetailedSlots && (
        <div className="mb-3 rounded-2xl bg-white p-3 shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
          <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
            Slot Assignment Mode
          </h3>
          <div className="flex w-full rounded-xl bg-gray-100 p-1 mb-3 gap-1">
            <button
              type="button"
              onClick={() => handleModeChange("candidate")}
              className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-all ${
                assignMode === "candidate"
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Let candidates choose
            </button>
            <button
              type="button"
              onClick={() => handleModeChange("recruiter")}
              className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-all ${
                assignMode === "recruiter"
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Assign slots now
            </button>
          </div>
          <p className="text-xs text-blue-800 bg-blue-50 py-2 px-3 rounded-xl leading-relaxed">
            {assignMode === "candidate"
              ? "Candidates receive a booking link and pick their own slot."
              : "Candidates receive a single email with their slot already booked."}
          </p>
        </div>
      )}

      {assignMode === "candidate" && (
        <div className="mt-2">
          {panelGroups.length > 0 && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="flex items-center gap-2 font-bold text-base text-gray-800 dark:text-gray-100">
                  <UsersIcon className="h-5 w-5 text-entntblue" />
                  Interview Panels
                </h3>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-[11px] font-medium text-entntblue hover:underline"
                >
                  {selectedPanels.size === panelGroups.length ? "Deselect all" : "Select all"}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {panelGroups.map((group) => {
                  const active = selectedPanels.has(group.panel);
                  return (
                    <button
                      type="button"
                      key={group.panel}
                      onClick={() => handlePanelToggle(group.panel)}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-all ${
                        active
                          ? "bg-entntblue text-white border-entntblue shadow-md"
                          : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      {active && <CheckCircleIcon className="h-3.5 w-3.5" />}
                      {group.panel}
                      <span className={active ? "font-bold text-white" : "font-bold text-gray-500 dark:text-gray-400"}>
                        · {group.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <h4 className="flex items-center gap-1.5 font-bold text-sm text-gray-800 dark:text-gray-100 mb-1.5 mt-3">
            <CalendarDaysIcon className="h-4 w-4 text-entntblue" />
            Available Slots
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              {availableSlots.length > 0 ? (
                <div className="flex flex-col gap-1.5">
                  {availableSlots.map((slot) => {
                    const over = (slot.selectedCount ? slot.selectedCount : 0) > slot.adjustedCount;
                    // detailedInterviewSlots holds every physically-unbooked slot; adjustedCount subtracts
                    // slots implicitly held for candidates already at this stage without a booking yet —
                    // the gap between the two is what's "reserved", not actually unavailable.
                    const rawOpenCount = interviewSlotsForStatus(slot.roleStatusId).length;
                    const reservedCount = Math.max(rawOpenCount - slot.adjustedCount, 0);
                    return (
                      <div
                        key={slot.roleStatusId}
                        className={`rounded-lg border p-2.5 transition-shadow hover:shadow-sm ${
                          over
                            ? "border-red-500 border-2 bg-red-50 dark:bg-red-900/20 ring-4 ring-red-500/10"
                            : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80"
                        }`}
                      >
                        <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">{slot.roleStatusName}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                          {slot.adjustedCount} available ·{" "}
                          <span className={over ? "text-red-600 font-bold bg-red-100 dark:bg-red-900/50 px-1 py-0.5 rounded" : "font-medium"}>
                            {slot.selectedCount ?? 0} selected
                          </span>
                        </p>
                        {reservedCount > 0 && (
                          <p className="mt-1.5 flex items-start gap-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-1.5 py-1">
                            <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                            <span>
                              {rawOpenCount} slot{rawOpenCount === 1 ? "" : "s"} physically open, but {reservedCount}{" "}
                              {reservedCount === 1 ? "is" : "are"} already reserved for candidate{reservedCount === 1 ? "" : "s"}{" "}
                              already at this stage awaiting a booking.
                            </span>
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 py-3 text-gray-400">
                  <InboxIcon className="h-4 w-4" />
                  <p className="text-xs">No available slots found.</p>
                </div>
              )}
            </div>

            {detailedAssessmentSlots.length > 0 && (
              <div>
                <div className="flex flex-col gap-1.5">
                  {detailedAssessmentSlots.map((slot) => (
                    <div
                      key={slot.id}
                      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 p-2.5"
                    >
                      <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                        {slot.roleStatusName}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                        {formatSlotRange(slot.startTime, slot.endTime).date}, {formatSlotRange(slot.startTime, slot.endTime).time}
                        {slot.assessmentName ? ` · ${slot.assessmentName}` : ""}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {assignMode === "candidate" && (
        <div className="mt-3">
          {sectionsToRender.map((bucket, idx) => {
            const members = bucketedCandidates[bucket];
            const isExpanded = expandedSections[bucket];
            const slotsSplit = splitByMinority(members, slotsIsOn);
            const emailSplit = splitByMinority(members, emailIsOn);
            const exceptionMembers = dedupeMembers(members, [slotsSplit.minority, emailSplit.minority]);
            const allAreExceptions = exceptionMembers.length === members.length;

            return (
              <div
                key={bucket}
                className={idx > 0 ? "mt-4 pt-4 border-t border-gray-200 dark:border-gray-700" : ""}
              >
                {renderSectionHeader(bucket, members.length)}
                {isExpanded && (
                  <>
                    {bucket === "other" ? (
                      <>
                        <p className="mb-2 px-1 text-xs font-medium text-gray-700 dark:text-gray-200">
                          {members.length} candidate{members.length === 1 ? "" : "s"}, no slot required
                          {" — "}
                          {renderExceptionsLink("other", "view list")}
                        </p>
                        {revealedExceptions.other && (
                          <>
                            {renderColumnHeader("Slots")}
                            <ul className="space-y-2 pb-2">{members.map((sc) => renderCandidateModeRow(sc))}</ul>
                          </>
                        )}
                      </>
                    ) : (() => {
                      const hasExceptions = exceptionMembers.length > 0 && !allAreExceptions;
                      const rowsToDisplay = hasExceptions ? exceptionMembers : members;
                      return (
                      <>
                        <p className="mb-2 px-1 text-xs font-medium text-gray-700 dark:text-gray-200">
                          {members.length} candidate{members.length === 1 ? "" : "s"} · {describeSplit("slots", slotsSplit, members.length)} · {describeSplit("email", emailSplit, members.length)}
                          {!allAreExceptions && (
                            <>
                              {" — "}
                              {renderExceptionsLink(bucket, hasExceptions ? "view them" : "view list")}
                            </>
                          )}
                        </p>
                        {(allAreExceptions || revealedExceptions[bucket]) && (
                          <>
                            {renderColumnHeader("Slots")}
                            <ul className="space-y-2 pb-2">
                              {rowsToDisplay.map((sc) => renderCandidateModeRow(sc))}
                            </ul>
                          </>
                        )}
                      </>
                      );
                    })()}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {assignMode === "recruiter" && (
        <div className="mt-3">
          {sectionsToRender.map((bucket, idx) => {
            const members = bucketedCandidates[bucket];
            const isExpanded = expandedSections[bucket];
            const emailSplit = splitByMinority(members, emailIsOn);
            const noSlotConfiguredMembers =
              bucket === "assessment"
                ? members.filter((sc) => {
                    const action = sc.actions?.[actionIndex];
                    return action ? !assessmentRoleStatusIds.has(action.roleStatusId) : false;
                  })
                : bucket === "interview"
                ? members.filter((sc) => {
                    const action = sc.actions?.[actionIndex];
                    return action ? interviewSlotsForStatus(action.roleStatusId).length === 0 : false;
                  })
                : [];
            const exceptionMembers = dedupeMembers(members, [noSlotConfiguredMembers, emailSplit.minority]);
            const allAreExceptions = exceptionMembers.length === members.length;
            return (
              <div
                key={bucket}
                className={idx > 0 ? "mt-4 pt-4 border-t border-gray-200 dark:border-gray-700" : ""}
              >
                {renderSectionHeader(bucket, members.length)}
                {isExpanded && (
                <>
                {bucket === "assessment" && (() => {
                  const missingAssessmentStageNames = Array.from(new Set(
                    members
                      .map((sc) => sc.actions?.[actionIndex])
                      .filter((action): action is CandidateStatus => !!action && !assessmentRoleStatusIds.has(action.roleStatusId))
                      .map((action) => roleStatuses.find((rs) => rs.id === action.roleStatusId)?.name ?? "this stage")
                  ));
                  return (
                  <div className="mb-3">
                    {detailedAssessmentSlots.length > 0 ? (
                      assessmentPickerCollapsed && selectedAssessmentSlot ? (
                        <div className="flex items-center justify-between gap-3 rounded-2xl bg-white p-3 shadow-[0_4px_15px_rgb(0,0,0,0.05)]">
                          <div className="flex items-center gap-3 min-w-0">
                            <CalendarDaysIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {formatSlotRange(selectedAssessmentSlot.startTime, selectedAssessmentSlot.endTime).date},{" "}
                                {formatSlotRange(selectedAssessmentSlot.startTime, selectedAssessmentSlot.endTime).time}
                              </p>
                              <p className="text-xs font-medium text-gray-900 truncate">
                                {selectedAssessmentSlot.roleStatusName}
                                {selectedAssessmentSlot.assessmentName ? ` · ${selectedAssessmentSlot.assessmentName}` : ""} · applies to all{" "}
                                {members.length} candidate{members.length === 1 ? "" : "s"}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setForceExpandAssessment(true)}
                            className="flex-shrink-0 text-sm font-semibold text-entntblue hover:underline"
                          >
                            Change
                          </button>
                        </div>
                      ) : (
                        <>
                          <h4 className="flex items-center gap-2 font-bold text-base text-gray-900 mb-2">
                            <CalendarDaysIcon className="h-5 w-5 text-blue-600" />
                            Assessment Slot
                            <span className="text-[10px] font-medium bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full ml-1">applies to everyone</span>
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {detailedAssessmentSlots.map((slot) => {
                              const active = selectedAssessmentSlotId === slot.id;
                              return (
                                <button
                                  type="button"
                                  key={slot.id}
                                  onClick={() => handleAssessmentSlotChange(slot.id)}
                                  className={`text-left rounded-2xl p-3 transition-all w-full max-w-[240px] ${
                                    active
                                      ? "bg-white shadow-[0_8px_30px_rgb(0,0,0,0.08)] ring-2 ring-blue-600"
                                      : "bg-white shadow-[0_4px_15px_rgb(0,0,0,0.05)] border border-transparent hover:border-blue-200"
                                  }`}
                                >
                                  <div className="flex flex-col gap-1">
                                    <span className="text-2xl font-semibold text-gray-900">
                                      {formatSlotRange(slot.startTime, slot.endTime).date}
                                    </span>
                                    <p className="text-[13px] text-gray-600 mt-2">
                                      {slot.roleStatusName} · {formatSlotRange(slot.startTime, slot.endTime).time}
                                    </p>
                                    {slot.assessmentName && (
                                      <p className="text-[13px] text-gray-600">
                                        {slot.assessmentName}
                                      </p>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                          <p className="text-sm font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                            Selecting a slot above assigns it to every candidate below whose stage has a matching slot.
                          </p>
                        </>
                      )
                    ) : (
                      <div className="flex flex-col items-center gap-1 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 py-4 text-gray-400">
                        <InboxIcon className="h-5 w-5" />
                        <p className="text-xs">
                          No assessment slots have been created for {missingAssessmentStageNames.join(", ")} yet. Create slots first, or use "Let candidates choose" instead.
                        </p>
                      </div>
                    )}
                    {detailedAssessmentSlots.length > 0 && missingAssessmentStageNames.length > 0 && (
                      <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span>
                          No assessment slots have been created yet for: {missingAssessmentStageNames.join(", ")}. These candidates are marked below — create slots first, or use "Let candidates choose" instead.
                        </span>
                      </div>
                    )}
                  </div>
                  );
                })()}

                {bucket === "interview" && (() => {
                  const missingInterviewStageNames = Array.from(new Set(
                    members
                      .map((sc) => sc.actions?.[actionIndex])
                      .filter((action): action is CandidateStatus => !!action && interviewSlotsForStatus(action.roleStatusId).length === 0)
                      .map((action) => roleStatuses.find((rs) => rs.id === action.roleStatusId)?.name ?? "this stage")
                  ));
                  return (
                  <div className="mb-3">
                    <div className="flex items-center justify-between">
                      <h4 className="flex items-center gap-2 font-bold text-base text-gray-900">
                        <UserGroupIcon className="h-5 w-5 text-blue-600" />
                        Interview Slots
                      </h4>
                      <div className="flex items-center gap-2">
                        {interviewAutoAssignAttempted && interviewTotalCount > 0 && interviewAssignedCount < interviewTotalCount && (
                          <span className="text-[10px] font-medium text-gray-500">
                            {interviewAssignedCount} of {interviewTotalCount} assigned
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={handleAutoAssignInterviewSlots}
                          disabled={detailedInterviewSlots.length === 0}
                          className="inline-flex items-center gap-1 rounded-md bg-green-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-green-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          Auto-assign all
                        </button>
                      </div>
                    </div>
                    {missingInterviewStageNames.length > 0 && (
                      <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span>
                          No interview slots have been created yet for: {missingInterviewStageNames.join(", ")}. These candidates are marked below — create slots first, or use "Let candidates choose" instead.
                        </span>
                      </div>
                    )}
                    {interviewAutoAssignAttempted && interviewUnassignedCount > 0 && (
                      <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span>
                          Only {interviewAssignedCount} of {interviewTotalCount} candidates could be auto-assigned — not enough available slots.{" "}
                          {interviewUnassignedCount} candidate{interviewUnassignedCount === 1 ? "" : "s"}{" "}
                          {interviewUnassignedCount === 1 ? "needs" : "need"} manual selection.
                        </span>
                      </div>
                    )}
                  </div>
                  );
                })()}

                {bucket === "interview" && (
                  <>
                    {renderColumnHeader("Assigned Slot")}
                    <ul className="space-y-2 pb-2">
                      {members.map((sc) => renderRecruiterRow(sc, bucket))}
                    </ul>
                  </>
                )}

                {bucket === "assessment" && (() => {
                  const hasExceptions = exceptionMembers.length > 0 && !allAreExceptions;
                  const rowsToDisplay = hasExceptions ? exceptionMembers : members;
                  return (
                  <div className="mb-1">
                    <p className="px-1 text-xs font-medium text-gray-700 dark:text-gray-200">
                      {members.length} candidate{members.length === 1 ? "" : "s"}
                      {selectedAssessmentSlot ? " · all use the selected slot" : ""} · {describeSplit("email", emailSplit, members.length)}
                      {!allAreExceptions && (
                        <>
                          {" — "}
                          {renderExceptionsLink("assessment", hasExceptions ? "view them" : "view list")}
                        </>
                      )}
                    </p>
                    {(allAreExceptions || revealedExceptions.assessment) && (
                      <>
                        {renderColumnHeader("Assigned Slot")}
                        <ul className="space-y-2 pb-2 mt-2">
                          {rowsToDisplay.map((sc) => renderRecruiterRow(sc, bucket))}
                        </ul>
                      </>
                    )}
                  </div>
                  );
                })()}

                {bucket === "other" && (
                  <div className="mb-1">
                    <p className="px-1 text-xs font-medium text-gray-700 dark:text-gray-200">
                      {members.length} candidate{members.length === 1 ? "" : "s"}, no slot required
                      {" — "}
                      {renderExceptionsLink("other", "view list")}
                    </p>
                    {revealedExceptions.other && (
                      <>
                        {renderColumnHeader("Assigned Slot")}
                        <ul className="space-y-2 pb-2 mt-2">
                          {members.map((sc) => renderRecruiterRow(sc, bucket))}
                        </ul>
                      </>
                    )}
                  </div>
                )}
                </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
